<script setup>
import { ref, watch, nextTick, useTemplateRef } from "vue";
import { StealieDrawer } from "@get-overalls/fe-stealie";
import { XMarkIcon } from "@heroicons/vue/24/outline";
import { useRequestSurveyStore } from "@/stores/requestSurvey";
import MessageRow from "@/components/billybot/MessageRow.vue";
import BillyBotTyping from "@/components/billybot/BillyBotTyping.vue";
import useConfetti from "@/composables/useConfetti";
import RequestSummary from "@/components/billybot/RequestSummary.vue";
import CancelConfirmationPrompt from "@/components/billybot/CancelConfirmationPrompt.vue";
import FirstRequestHeader from "@/components/billybot/FirstRequestHeader.vue";
import StandardRequestHeader from "@/components/billybot/StandardRequestHeader.vue";
import RequestReceivedPrompt from "@/components/billybot/RequestReceivedPrompt.vue";

const isOpen = defineModel("open", { required: true, default: false, type: Boolean });

const requestSurveyStore = useRequestSurveyStore();
const drawerChildRef = useTemplateRef("drawerChildRef");
const drawerScrollPosition = ref(0);
const showCancelConfirmation = ref(false);
const showRequestReceived = ref(false);
const showSummary = ref(false);
const submittingSurvey = ref(false);
const { party } = useConfetti();

const closeDrawer = () => {
  isOpen.value = false;
  showCancelConfirmation.value = false;
  showSummary.value = false;
  submittingSurvey.value = false;
  showRequestReceived.value = false;
};

const handleResponse = async (response) => {
  const userResponse = {
    questionId: response.questionId,
    answer: response.answer,
    customAnswerDisplay: response.customAnswerDisplay,
    finalQuestion: response.finalQuestion,
  };

  await requestSurveyStore.answerQuestion(userResponse, response.isEdit);

  if (response.finalQuestion) {
    showSummary.value = true;
  } else {
    scrollToPosition("bottom");
  }
};

const handleEditResponse = (response) => {
  handleResponse({ ...response, isEdit: true });
};

const handleSubmitRequest = async () => {
  submittingSurvey.value = true;
  await requestSurveyStore.completeSurvey();
  showRequestReceived.value = true;
  if (requestSurveyStore.isFirstRequest) {
    party();
  }
};

const scrollToPosition = (position, behavior = "smooth") => {
  nextTick(() => {
    if (drawerChildRef.value && drawerChildRef.value.closest) {
      const container = drawerChildRef.value.closest("[data-pc-section=content]");
      if (container) {
        let scrollPosition;
        if (typeof position === "string") {
          scrollPosition = position === "bottom" ? container.scrollHeight : 0;
        } else {
          scrollPosition = position;
        }
        container.scrollTo({
          top: scrollPosition,
          behavior,
        });
      }
    }
  });
};

// Scroll to the bottom to show the typing indicator
watch(
  () => requestSurveyStore.loading,
  (newVal) => {
    if (newVal && !showSummary.value) {
      scrollToPosition("bottom");
    }
  },
);

const handleClose = (e) => {
  if (showRequestReceived.value) {
    closeDrawer();
    return;
  }
  if (e.type === "keydown") {
    if (e.key !== "Escape") {
      return;
    }
    // If the user is focused on an input, the first ESC press will blur
    // the input and a second ESC press will show the cancel confirmation
    if (e.target?.closest("[data-pc-section=content]")) {
      e.target.blur();
      return;
    }
  }
  if (drawerChildRef?.value.closest) {
    const container = drawerChildRef.value.closest("[data-pc-section=content]");
    if (container) {
      drawerScrollPosition.value = container.scrollTop;
    }
  }
  showCancelConfirmation.value = true;
};
const handleStay = () => {
  showCancelConfirmation.value = false;
  if (drawerScrollPosition.value) {
    scrollToPosition(drawerScrollPosition.value, "instant");
  }
};

watch(isOpen, (newVal) => {
  if (newVal) {
    document.addEventListener("keydown", handleClose);
  } else {
    document.removeEventListener("keydown", handleClose);
  }
});
</script>

<template>
  <StealieDrawer v-model:open="isOpen" scrollable full-bleed data-sr-unmask>
    <div ref="drawerChildRef" class="hidden"></div>
    <RequestReceivedPrompt
      v-if="showRequestReceived"
      :category="requestSurveyStore.surveyDetails.category"
      :request-name="requestSurveyStore.surveyDetails.name"
      @confirm="closeDrawer"
    />
    <CancelConfirmationPrompt
      v-else-if="showCancelConfirmation"
      @confirm="closeDrawer"
      @cancel="handleStay"
    />
    <template v-else-if="showSummary">
      <RequestSummary
        :questions="requestSurveyStore.surveyQuestions"
        :category="requestSurveyStore.surveyDetails.category.name"
        :submitting="submittingSurvey"
        @response:edit="handleEditResponse"
        @submit-request="handleSubmitRequest"
      />
    </template>
    <template v-else>
      <FirstRequestHeader v-if="requestSurveyStore.isFirstRequest" />
      <StandardRequestHeader v-else />
      <MessageRow
        v-for="question in requestSurveyStore.surveyQuestions"
        :key="question.id"
        :category="requestSurveyStore.surveyDetails.category.name"
        :question="question"
        @response="handleResponse"
        @response:edit="handleEditResponse"
      />
      <BillyBotTyping :loading="requestSurveyStore.loading" />
    </template>
    <button
      v-if="!showCancelConfirmation"
      class="absolute top-4 right-4 z-20 flex size-8 cursor-pointer items-center justify-center rounded-full bg-transparent text-gray-500 transition duration-300 hover:bg-gray-500/8 hover:text-black focus-visible:ring focus-visible:ring-focus-highlight-light focus-visible:outline-none"
      @click="handleClose"
    >
      <XMarkIcon class="size-5" />
    </button>
  </StealieDrawer>
</template>
